import React, { useState } from 'react';
import { 
  LayoutDashboard, 
  Package, 
  ShoppingCart, 
  Users, 
  Settings, 
  ChevronLeft, 
  ChevronRight,
  Store,
  ShoppingBag,
  Archive,
  Receipt,
  Calculator,
  PieChart,
  Contact,
  ChevronDown,
  UserCog,
  Shirt,
  Box,
  AlertCircle,
  Scissors,
  Warehouse,
  Sparkles,
  Factory,
  FlaskConical,
  Plus,
  Shield,
  Scale,
} from 'lucide-react';
import { useNavigation } from '../../context/NavigationContext';
import { useSettings } from '../../context/SettingsContext';
import { useSupabase } from '../../context/SupabaseContext';
import { useCheckPermission } from '../../hooks/useCheckPermission';
import { canAccessTechnicalDeveloperSettings } from '@/app/lib/developerAccountingAccess';
import { canAccessAccountingDeveloperCenter } from '@/app/lib/accountingDeveloperCenterAccess';
import { leaveSpecialAppRoute } from '@/app/lib/specialRouteNavigation';
import { buildNavOpenUrl, syncNavUrlToHistory } from '@/app/lib/navDeepLinks';
import {
  ContextMenu,
  ContextMenuContent,
  ContextMenuItem,
  ContextMenuTrigger,
} from '@/app/components/ui/context-menu';
import { clsx } from 'clsx';
import { motion, AnimatePresence } from 'motion/react';

type NavItem = {
  id: string;
  label: string;
  icon: any;
  children?: { id: string; label: string }[];
  isHidden?: boolean;
};

export const Sidebar = () => {
  const { currentView, setCurrentView, isSidebarOpen, toggleSidebar, openDrawer, setPartyLedgerParams } = useNavigation();
  const { modules: settingsModules, featureFlags, isPermissionLoaded, businessSettings } = useSettings();
  const { hasPermission } = useCheckPermission();
  const { userRole } = useSupabase();
  const developerToolsNavAllowed = canAccessTechnicalDeveloperSettings(userRole);
  const accountingDeveloperCenterAllowed = canAccessAccountingDeveloperCenter(userRole);
  const studioProductionV2 = featureFlags?.studio_production_v2 === true;
  const studioProductionV3 = featureFlags?.studio_production_v3 === true;
  const [expandedItems, setExpandedItems] = useState<string[]>([]);
  const pathname = typeof window !== 'undefined' ? window.location.pathname : '';

  const expensesNavVisible =
    (settingsModules.expensesModuleEnabled && hasPermission('expenses.view')) ||
    (settingsModules.accountingModuleEnabled && hasPermission('accounting.view'));

  const toggleExpand = (id: string) => {
    setExpandedItems(prev => 
      prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]
    );
  };

  const navItems: NavItem[] = [
    { id: 'dashboard', label: 'Dashboard', icon: LayoutDashboard },
    { id: 'contacts', label: 'Contacts', icon: Users, isHidden: !hasPermission('contacts.view') },
    { id: 'products', label: 'Products', icon: Package, isHidden: !hasPermission('products.view') },
    {
      id: 'inventory-group',
      label: 'Inventory',
      icon: Warehouse,
      isHidden: !hasPermission('inventory.view'),
      children: [
        { id: 'inventory', label: 'Stock Overview' },
        { id: 'inventory-count', label: 'Inventory Count' },
        { id: 'stock-report', label: 'Stock Report' },
        { id: 'stock-movement-history', label: 'Stock Ledger by Product' },
      ],
    },
    { id: 'purchases', label: 'Purchases', icon: ShoppingBag, isHidden: !settingsModules.purchasesModuleEnabled || !hasPermission('purchases.view') },
    { id: 'sales', label: 'Sales', icon: ShoppingCart, isHidden: !settingsModules.salesModuleEnabled || !hasPermission('sales.view') },
    { id: 'bespoke-work-orders', label: 'Work Orders', icon: Scissors, isHidden: !settingsModules.salesModuleEnabled || !hasPermission('sales.view') || !businessSettings.enableBespokeOrders },
    { id: 'rentals', label: 'Rentals', icon: Shirt, isHidden: !settingsModules.rentalModuleEnabled || !hasPermission('rentals.view') },
    { id: 'pos', label: 'POS System', icon: Store, isHidden: !settingsModules.posModuleEnabled || !hasPermission('pos.view') },
    { 
      id: 'studio-group', 
      label: 'Studio Production', 
      icon: Factory,
      isHidden: (!settingsModules.studioModuleEnabled && !studioProductionV2 && !studioProductionV3) || !hasPermission('studio.view'),
      children: [
        { id: 'studio-dashboard-new', label: 'Dashboard' },
        { id: 'studio', label: 'Studio Sales' },
        { id: 'studio-pipeline', label: 'Production Pipeline' },
        { id: 'studio-workflow', label: 'Workers' },
      ]
    },
    {
      id: 'manufacturing-group',
      label: 'Manufacturing',
      icon: Box,
      isHidden: !settingsModules.productionModuleEnabled || !hasPermission('studio.view'),
      children: [
        { id: 'manufacturing-bom', label: 'Bill of Materials' },
        { id: 'manufacturing-orders', label: 'Production Orders' },
        { id: 'manufacturing-workflow', label: 'Production Workflow' },
      ]
    },
    { id: 'expenses', label: 'Expenses', icon: Receipt, isHidden: !expensesNavVisible },
    {
      id: 'accounting-group',
      label: 'Accounting',
      icon: Calculator,
      isHidden: !settingsModules.accountingModuleEnabled || !hasPermission('accounting.view'),
      children: [
        { id: 'accounting', label: 'Accounting Dashboard' },
        { id: 'party-ledger', label: 'Party Ledger' },
      ],
    },
    {
      id: 'ar-ap-reconciliation-center',
      label: 'AR/AP Diagnostics',
      icon: Scale,
      isHidden: !settingsModules.accountingModuleEnabled || !hasPermission('accounting.view'),
    },
    { id: 'reports', label: 'Reports', icon: PieChart, isHidden: !settingsModules.reportsModuleEnabled || !hasPermission('reports.view') },
    { id: 'settings', label: 'Settings', icon: Settings, isHidden: !hasPermission('settings.view') },
    {
      id: 'test-pages-group',
      label: 'Developer Tools',
      icon: FlaskConical,
      isHidden: !developerToolsNavAllowed,
      children: [
        { id: 'permission-inspector', label: 'Permission Inspector' },
        { id: 'test-account-entry', label: 'Account Entry' },
        { id: 'accounting-edit-trace', label: 'Accounting Edit Trace (Unified)' },
        { id: 'ar-ap-truth-lab', label: 'AR / AP Truth Lab (canonical)' },
        { id: 'expense-edit-trace', label: 'Expense Edit Trace' },
        { id: 'customer-ledger-test', label: 'Legacy: Customer Ledger Test' },
        { id: 'ledger-debug-test', label: 'Ledger Debug (RPC vs API)' },
        { id: 'test-ledger', label: 'Test Ledger (API Tests)' },
        { id: 'customer-ledger-interactive-test', label: 'Interactive Test (Manual)' },
        { id: 'contact-search-test', label: 'Contact Test' },
        { id: 'sale-header-test', label: 'Header Test' },
        { id: 'purchase-header-test', label: 'Purchase Header Test' },
        { id: 'transaction-header-test', label: 'Transaction Header' },
        { id: 'user-management-test', label: 'User Management Test' },
        { id: 'branch-management-test', label: 'Branch Management Test' },
        { id: 'accounting-chart-test', label: 'Chart of Accounts Test' },
        { id: 'rls-validation', label: 'RLS Validation' },
        { id: 'accounting-integrity-lab', label: 'Accounting Integrity Lab' },
        { id: 'developer-integrity-lab', label: 'Developer Integrity Lab' },
        ...(accountingDeveloperCenterAllowed
          ? [{ id: 'accounting-developer-center', label: 'Accounting Developer Center' }]
          : []),
        { id: 'day4-certification', label: 'Day 4 Certification' },
        { id: 'erp-integration-test', label: 'ERP Integration Test' },
        { id: 'cutover-prep', label: 'Cutover Prep' },
        { id: 'responsive-test', label: 'Responsive Test' },
      ]
    },
  ];

  const visibleNavItems = navItems.filter(item => (isPermissionLoaded ? !item.isHidden : item.id === 'dashboard'));

  const handleItemClick = (item: NavItem) => {
    if (item.children) {
      if (!isSidebarOpen) toggleSidebar();
      toggleExpand(item.id);
    } else {
      navigateToView(item.id);
    }
  };

  const navigateToView = (viewId: string) => {
    if (viewId === 'party-ledger') {
      setPartyLedgerParams?.(null);
    }
    setCurrentView(viewId as any);
    if (typeof window !== 'undefined') {
      leaveSpecialAppRoute('/');
      syncNavUrlToHistory(viewId);
    }
  };

  /** Parent groups without a landing page open their first child in a new tab. */
  const resolveNewTabViewId = (item: NavItem): string => {
    if (item.children?.length) return item.children[0].id;
    return item.id;
  };

  const openNavInNewTab = (viewId: string) => {
    window.open(buildNavOpenUrl(viewId), '_blank', 'noopener,noreferrer');
  };

  const renderNavContextMenu = (viewId: string, trigger: React.ReactNode, onOpen: () => void) => (
    <ContextMenu>
      <ContextMenuTrigger asChild>{trigger}</ContextMenuTrigger>
      <ContextMenuContent className="w-48 bg-popover border-border text-popover-foreground">
        <ContextMenuItem
          className="cursor-pointer focus:bg-accent focus:text-accent-foreground"
          onSelect={() => onOpen()}
        >
          Open
        </ContextMenuItem>
        <ContextMenuItem
          className="cursor-pointer focus:bg-accent focus:text-accent-foreground"
          onSelect={() => openNavInNewTab(viewId)}
        >
          Open in new tab
        </ContextMenuItem>
      </ContextMenuContent>
    </ContextMenu>
  );

  return (
    <motion.aside 
      initial={false}
      animate={{ width: isSidebarOpen ? 260 : 80 }}
      className="hidden md:flex flex-col h-screen bg-sidebar border-r border-sidebar-border text-sidebar-foreground transition-all duration-300 relative z-20 shrink-0"
    >
      <div className="p-4 flex items-center justify-between h-16 border-b border-sidebar-border">
        <div className="flex items-center gap-3 overflow-hidden whitespace-nowrap">
          <div className="w-8 h-8 bg-blue-600 rounded-lg flex items-center justify-center shrink-0 text-foreground shadow-lg shadow-blue-900/20">
            <span className="font-bold text-lg">E</span>
          </div>
          {isSidebarOpen && (
            <motion.span 
              initial={{ opacity: 0 }} 
              animate={{ opacity: 1 }}
              className="font-bold text-lg tracking-tight"
            >
              ERP Master
            </motion.span>
          )}
        </div>
        <button 
          onClick={toggleSidebar}
          className="p-1.5 hover:bg-sidebar-accent rounded-lg text-muted-foreground hover:text-sidebar-foreground transition-colors absolute -right-3 top-6 bg-sidebar border border-sidebar-border shadow-sm"
        >
          {isSidebarOpen ? <ChevronLeft size={14} /> : <ChevronRight size={14} />}
        </button>
      </div>

      <nav className="flex-1 py-6 px-3 space-y-1 overflow-y-auto">
        {visibleNavItems.map((item) => {
          const isExpanded = expandedItems.includes(item.id);
          const isActive =
            currentView === item.id ||
            item.children?.some((c) => c.id === currentView) ||
              (item.id === 'test-pages-group' &&
              (pathname === '/admin/permission-inspector' ||
                pathname === '/admin/developer-integrity-lab' ||
                pathname === '/admin/accounting-developer-center' ||
                pathname === '/admin/accounting-test-bench' ||
                pathname === '/test/accounting-edit-trace' ||
                pathname === '/test/ar-ap-truth-lab' ||
                pathname === '/test/expense-edit-trace' ||
                pathname === '/test/accounting-accounts-hierarchy'));
          
          return (
            <div key={item.id}>
              <div className="flex items-center gap-1 w-full group/item">
                {renderNavContextMenu(
                  resolveNewTabViewId(item),
                  <button
                    onClick={() => handleItemClick(item)}
                    className={clsx(
                      "flex-1 flex items-center justify-between p-3 rounded-xl transition-all duration-200 group relative min-w-0",
                      isActive && !item.children
                        ? "bg-primary text-primary-foreground shadow-lg shadow-primary/20"
                        : "text-muted-foreground hover:bg-sidebar-accent hover:text-sidebar-foreground"
                    )}
                  >
                    <div className="flex items-center gap-3 min-w-0">
                      <item.icon size={20} strokeWidth={1.5} className={clsx("shrink-0", isActive && "text-primary-foreground")} />
                      
                      {isSidebarOpen ? (
                        <motion.span 
                          initial={{ opacity: 0 }} 
                          animate={{ opacity: 1 }}
                          className="font-medium whitespace-nowrap text-sm truncate flex items-center gap-1.5"
                        >
                          {item.label}
                          {item.id === 'studio-group' && studioProductionV3 && (
                            <span className="shrink-0 text-[10px] font-semibold px-1.5 py-0.5 rounded bg-emerald-500/20 text-emerald-400">V3</span>
                          )}
                          {item.id === 'studio-group' && studioProductionV2 && !studioProductionV3 && (
                            <span className="shrink-0 text-[10px] font-semibold px-1.5 py-0.5 rounded bg-amber-500/20 text-amber-400">V2</span>
                          )}
                        </motion.span>
                      ) : (
                        <div className="absolute left-14 bg-popover text-popover-foreground text-sm px-2 py-1 rounded opacity-0 group-hover:opacity-100 pointer-events-none transition-opacity border border-border whitespace-nowrap z-50 shadow-xl flex items-center gap-1.5">
                          {item.label}
                          {item.id === 'studio-group' && studioProductionV3 && <span className="text-emerald-400 text-xs font-semibold">V3</span>}
                          {item.id === 'studio-group' && studioProductionV2 && !studioProductionV3 && <span className="text-amber-400 text-xs font-semibold">V2</span>}
                        </div>
                      )}
                    </div>
                    
                    {item.children && isSidebarOpen && (
                      <ChevronDown 
                        size={16} 
                        className={clsx("shrink-0 transition-transform duration-200", isExpanded && "rotate-180")} 
                      />
                    )}
                  </button>,
                  () => {
                    if (item.children) {
                      handleItemClick(item);
                    } else {
                      navigateToView(item.id);
                    }
                  }
                )}
                {/* Quick Add / Import for Contacts */}
                {item.id === 'contacts' && isSidebarOpen && (
                  <div className="flex gap-0.5 opacity-0 group-hover/item:opacity-100 transition-opacity shrink-0">
                    <button
                      onClick={(e) => { e.stopPropagation(); openDrawer('addContact'); }}
                      className="p-1.5 rounded-lg hover:bg-sidebar-accent text-muted-foreground hover:text-sidebar-foreground"
                      title="Add Contact"
                    >
                      <Plus size={14} />
                    </button>
                  </div>
                )}
              </div>

              {/* Submenu */}
              <AnimatePresence>
                {item.children && isExpanded && isSidebarOpen && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: 'auto', opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    className="overflow-hidden"
                  >
                    <div className="pl-11 py-1 space-y-1">
                      {item.children.map((child) => (
                        <React.Fragment key={child.id}>
                          {renderNavContextMenu(
                            child.id,
                            <button
                              onClick={() => navigateToView(child.id)}
                              className={clsx(
                                "w-full text-left py-2 px-3 text-sm rounded-lg transition-colors",
                                currentView === child.id ||
                                  (child.id === 'accounting-edit-trace' && pathname === '/test/accounting-edit-trace') ||
                                  (child.id === 'ar-ap-truth-lab' && pathname === '/test/ar-ap-truth-lab') ||
                                  (child.id === 'expense-edit-trace' && pathname === '/test/expense-edit-trace')
                                  ? "text-primary bg-primary/10 font-medium"
                                  : "text-muted-foreground hover:text-sidebar-foreground hover:bg-sidebar-accent/50"
                              )}
                            >
                              {child.label}
                            </button>,
                            () => navigateToView(child.id)
                          )}
                        </React.Fragment>
                      ))}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          );
        })}
      </nav>
    </motion.aside>
  );
};